import { useEffect, useState } from "react";
import axios from "axios";
import { Flame, Trophy, Lightbulb, TrendingUp, Calendar } from "lucide-react";
import SlotBanner from "@/components/SlotBanner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Free Tip of the Day card — dark navy + emerald + gold, matching the
 * site header. Content managed via the admin panel. Renders nothing when
 * disabled or unavailable.
 */
export default function FreeTipCard() {
  const [tip, setTip] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/free-tip`)
      .then(({ data }) => setTip(data))
      .catch(() => setTip(null));
  }, []);

  if (!tip || !tip.enabled) return null;

  return (
    <section
      data-testid="free-tip-card"
      className="relative overflow-hidden rounded-2xl sm:rounded-3xl bg-ink-950 text-white shadow-2xl shadow-emerald-700/10 ring-1 ring-gold-500/20"
    >
      {/* Glow accents */}
      <div className="absolute -top-24 -right-20 w-[300px] h-[300px] rounded-full bg-emerald-700/30 blur-[100px] pointer-events-none" />
      <div className="absolute -bottom-24 -left-20 w-[280px] h-[280px] rounded-full bg-gold-500/15 blur-[100px] pointer-events-none" />

      <div className="relative p-6 sm:p-8 lg:p-10">
        {/* Ribbon */}
        <div className="flex items-center justify-between gap-3 mb-5 sm:mb-6">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-gold-500 text-ink-950 text-[10px] sm:text-xs font-bold uppercase tracking-widest">
            <Flame className="w-3.5 h-3.5" strokeWidth={3} />
            Free Tip of the Day
          </div>
          <div className="hidden sm:flex items-center gap-1.5 text-[10px] sm:text-xs font-mono uppercase tracking-widest text-emerald-500/80">
            <Calendar className="w-3.5 h-3.5" />
            Updated Daily
          </div>
        </div>

        {/* Match */}
        <div className="mb-6 sm:mb-8">
          <div className="text-[10px] sm:text-xs font-mono uppercase tracking-widest text-emerald-500/80 mb-2">
            Match
          </div>
          <h3
            data-testid="free-tip-match"
            className="font-display font-bold text-2xl sm:text-3xl md:text-4xl lg:text-5xl text-white tracking-tight leading-tight"
          >
            {tip.match}
          </h3>
        </div>

        {/* Tip + Odds side by side */}
        <div className="grid grid-cols-2 gap-3 sm:gap-4 mb-6 sm:mb-8">
          <div
            data-testid="free-tip-pick"
            className="p-4 sm:p-5 rounded-xl border border-white/10 bg-white/[0.04] backdrop-blur-sm"
          >
            <div className="flex items-center gap-1.5 text-[10px] sm:text-xs font-mono uppercase tracking-widest text-emerald-500/80 mb-1.5">
              <Trophy className="w-3.5 h-3.5" />
              Tip
            </div>
            <div className="font-display font-bold text-3xl sm:text-4xl lg:text-5xl gold-text tracking-tight">
              {tip.tip}
            </div>
          </div>
          <div
            data-testid="free-tip-odds"
            className="p-4 sm:p-5 rounded-xl border border-white/10 bg-white/[0.04] backdrop-blur-sm"
          >
            <div className="flex items-center gap-1.5 text-[10px] sm:text-xs font-mono uppercase tracking-widest text-emerald-500/80 mb-1.5">
              <TrendingUp className="w-3.5 h-3.5" />
              Odds
            </div>
            <div className="font-display font-bold text-3xl sm:text-4xl lg:text-5xl gold-text tracking-tight">
              {Number(tip.odds).toFixed(2)}
            </div>
          </div>
        </div>

        <div className="mb-6 sm:mb-8 empty:hidden">
          <SlotBanner slot="free_tip_card_middle" testId="free-tip-card-middle-slot-banner" pair />
        </div>

        {/* Analysis */}
        {tip.analysis && (
          <div
            data-testid="free-tip-analysis"
            className="p-4 sm:p-5 rounded-xl bg-white/[0.03] border border-white/10"
          >
            <div className="flex items-center gap-1.5 text-[10px] sm:text-xs font-mono uppercase tracking-widest text-emerald-500/80 mb-2">
              <Lightbulb className="w-3.5 h-3.5" />
              Analysis
            </div>
            <p className="text-sm sm:text-base text-white/85 leading-relaxed whitespace-pre-line">
              {tip.analysis}
            </p>
          </div>
        )}

        <div className="mt-6 sm:mt-8 empty:hidden">
          <SlotBanner slot="free_tip_card_bottom" testId="free-tip-card-bottom-slot-banner" pair />
        </div>
      </div>
    </section>
  );
}
